import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { RiFileExcel2Line, RiPrinterFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import StudentPrintCards from "../../../containers/superadmin/students/student-print-cards";
import { exportStudentsToExcel } from "../../../helpers/exportStudentsExcel";
import { getAllCoursesRequest } from "../../../modules/superadmin-modules/courses/Actions";
import { getStudentsByCourseId } from "../../../modules/admin-modules/students/Api";

const colourStyles = {
  control: (styles, { isFocused }) => ({
    ...styles,
    width: "100%",
    background: "#f5f5f5",
    color: "#333",
    borderRadius: "16px",
    borderColor: isFocused ? "#690000" : styles.borderColor,
    boxShadow: isFocused ? "0 0 0 1px #690000" : "none",
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "#333",
  }),
  placeholder: (styles) => ({
    ...styles,
    color: "#888",
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    color: isFocused || isSelected ? "#fff" : "#333",
    backgroundColor: isSelected
      ? "#690000"
      : isFocused
      ? "#8a1a1a"
      : "#fff",
    cursor: "pointer",
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 9999,
  }),
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 9999,
  }),
};

const ExportStudents = () => {
  const dispatch = useDispatch();
  const { courses } = useSelector((state) => state.courses);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: selectedCourse?.label
      ? `student-cards-${selectedCourse.label}`
      : "student-cards",
  });

  useEffect(() => {
    dispatch(getAllCoursesRequest());
  }, [dispatch]);

  const courseOptions =
    courses?.map((course) => ({
      value: course.id,
      label: course.name,
    })) || [];

  const fetchStudents = async (courseId) => {
    setIsLoading(true);
    setStudents([]);

    try {
      const response = await getStudentsByCourseId(courseId);
      const studentList = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];

      setStudents(studentList);
    } catch (error) {
      toast.error("Failed to load students");
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourseChange = (option) => {
    setSelectedCourse(option);

    if (option?.value) {
      fetchStudents(option.value);
      return;
    }

    setStudents([]);
  };

  const handleExport = async () => {
    if (!students.length) {
      toast.error("No students to export");
      return;
    }

    setIsExporting(true);

    try {
      const filename = selectedCourse?.label
        ? `students-${selectedCourse.label}`
        : "students-export";

      await exportStudentsToExcel(students, filename);
      toast.success("Students exported successfully");
    } catch (error) {
      toast.error("Failed to export students");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Flex
        w="100%"
        my="30px"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={4}
      >
        <Text fontSize="24px" fontWeight="600" color="main_1">
          تصدير بيانات الطلاب
        </Text>
        <LogoutMenu />
      </Flex>

      <Flex
        w="100%"
        alignItems={{ base: "stretch", md: "flex-end" }}
        direction={{ base: "column", md: "row" }}
        gap={4}
        mb="20px"
        position="relative"
        zIndex={2}
      >
        <Box w={{ base: "100%", md: "400px" }} flexShrink={0}>
          <Text mb={2} fontWeight="500">
            اختر الكورس
          </Text>
          <Select
            options={courseOptions}
            value={selectedCourse}
            onChange={handleCourseChange}
            styles={colourStyles}
            placeholder="اختر الكورس"
            isClearable
            isLoading={isLoading}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </Box>

        <Button
          leftIcon={<RiPrinterFill />}
          variant="outline"
          borderColor="main_1"
          color="main_1"
          borderRadius="16px"
          onClick={handlePrint}
          isDisabled={!selectedCourse || !students.length || isLoading}
          alignSelf={{ base: "stretch", md: "flex-end" }}
          w={{ base: "100%", md: "auto" }}
        >
          Print Cards
        </Button>

        <Button
          leftIcon={<RiFileExcel2Line />}
          color="white"
          bg="main_1"
          borderRadius="16px"
          onClick={handleExport}
          isLoading={isExporting}
          isDisabled={!selectedCourse || !students.length || isLoading}
          alignSelf={{ base: "stretch", md: "flex-end" }}
          w={{ base: "100%", md: "auto" }}
        >
          Export Cards to Excel
        </Button>
      </Flex>

      {selectedCourse && (
        <Text color="gray.600" mb={6}>
          {isLoading
            ? "Loading students..."
            : `${students.length} student(s) found`}
        </Text>
      )}

      {students.length > 0 && (
        <StudentPrintCards
          ref={printRef}
          students={students}
          courseName={selectedCourse?.label}
        />
      )}
    </Layout>
  );
};

export default ExportStudents;
